import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import usePageMetadata from '../utils/usePageMetadata';
import './Auth.css';

const Login = () => {
  usePageMetadata({
    title: 'Login | The Ad Shop',
    description:
      'Sign in to your Ad Shop account to access your unified analytics dashboard, compliance monitoring, and content performance insights.',
    canonicalUrl: 'https://theadshop.co/login',
    meta: [
      { property: 'og:title', content: 'Login | The Ad Shop' },
      {
        property: 'og:description',
        content:
          'Sign in to access your unified analytics dashboard with GA4, AdSense, and Search Console data.',
      },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: 'https://theadshop.co/login' },
      { name: 'twitter:card', content: 'summary' },
      { name: 'twitter:title', content: 'Login | The Ad Shop' },
      {
        name: 'twitter:description',
        content: 'Sign in to your Ad Shop account to access your analytics dashboard.',
      },
      { name: 'robots', content: 'noindex, follow' },
    ],
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Login to The Ad Shop',
      url: 'https://theadshop.co/login',
      description: 'Sign in page for The Ad Shop unified analytics platform.',
      isPartOf: {
        '@type': 'WebSite',
        name: 'The Ad Shop',
        url: 'https://theadshop.co/',
      },
    },
  });
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isOnboarding = searchParams.get('onboarding') === 'true';

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Clear error when user starts typing
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: '' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    try {
      const response = await axios.post('/api/auth/login', formData);
      login(response.data.user, response.data.token);
      // Redirect to sites page for onboarding, otherwise dashboard
      navigate(isOnboarding ? '/sites' : '/dashboard');
    } catch (error) {
      if (error.response?.status === 403 && error.response?.data?.status === 'pending') {
        navigate('/waitlist', { state: { email: formData.email } });
        setLoading(false);
        return;
      }
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        const errorMessage = error.response?.data?.message || 'Login failed. Please try again.';
        const requiresVerification = error.response?.data?.requiresVerification;
        
        if (requiresVerification) {
          setErrors({ 
            general: errorMessage,
            requiresVerification: true,
            email: formData.email
          });
        } else {
          setErrors({ general: errorMessage });
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const redirectToGoogleOAuth = (flowType) => {
    const state = { type: flowType };
    const redirectUri = `${window.location.origin}/auth/google-oauth-callback`;
    
    // Debug: log the redirect URI being used
    console.log('🔍 OAuth Redirect URI:', redirectUri);
    console.log('🔍 Current Origin:', window.location.origin);
    console.log('🔍 Full URL:', window.location.href);

    const params = new URLSearchParams({
      client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: 'email profile',
      access_type: 'offline',
      prompt: 'select_account', // Removed 'consent' - only show consent if permissions not already granted
      include_granted_scopes: 'true',
      state: JSON.stringify(state)
    });

    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
    window.location.href = authUrl;
  };

  const handleGoogleLogin = async () => {
    try {
      setLoading(true);
      
      // Clear any existing token before starting new OAuth flow
      // This ensures we don't accidentally use a cached token from a different account
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];

      // Always use redirect flow for reliability across browsers/devices
      redirectToGoogleOAuth('login');
    } catch (error) {
      console.error('Google OAuth error:', error);
      setErrors({ general: 'Google authentication failed. Please try again or use email login.' });
      setLoading(false);
    }
  };

  return (
    <div className="page-container">
      <div className="auth-container">
        <div className="auth-card">
        <div className="auth-header">
            <img src="/images/the-ad-shop-logo.svg" alt="The Ad Shop" className="auth-logo" />
          <h1>Welcome Back</h1>
          <p>Sign in to your account</p>
        </div>

        {errors.general && (
          <div className="error-message">
            {errors.general}
            {errors.requiresVerification && (
              <div style={{ marginTop: '1rem' }}>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const response = await axios.post('/api/auth/resend-verification', {
                        email: errors.email || formData.email
                      });
                      setErrors({ 
                        general: response.data.message || 'Verification email sent. Please check your inbox.',
                        email: errors.email || formData.email
                      });
                    } catch (error) {
                      setErrors({ 
                        general: error.response?.data?.message || 'Failed to resend verification email.',
                        email: errors.email || formData.email
                      });
                    }
                  }}
                  style={{
                    background: 'transparent',
                    border: '1px solid #10b981',
                    color: '#10b981',
                    padding: '0.5rem 1rem',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    marginTop: '0.5rem'
                  }}
                >
                  Resend Verification Email
                </button>
              </div>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={errors.email ? 'error' : ''}
              required
            />
            {errors.email && <span className="field-error">{errors.email}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className={errors.password ? 'error' : ''}
              required
            />
            {errors.password && <span className="field-error">{errors.password}</span>}
          </div>

          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <div className="auth-divider">
          <span>OR</span>
        </div>

        <button onClick={handleGoogleLogin} className="btn btn-google" disabled={loading}>
          {loading ? (
            <>
              <div className="spinner-small"></div>
              Connecting...
            </>
          ) : (
            <>
              <svg className="google-icon" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </>
          )}
        </button>

        <div className="auth-footer">
          <p>
            Don't have an account? <Link to="/signup">Sign up</Link>
          </p>
        </div>
      </div>
      </div>
    </div>
  );
};

export default Login;

